'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridDashedCenterProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridDashedCenter - Dashed grid pattern with center fade
 *
 * Uses CSS mask compositing to create a dashed intersection pattern
 * that fades from the center outward. Ideal for hero sections where
 * text is centered.
 *
 * Fixed fadeSize: 0.50
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridDashedCenter />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridDashedCenter({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridDashedCenterProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.5
  const size = `${fadeSize * 100}%`
  const maskGradient = `
    repeating-linear-gradient(to right, black 0px, black 3px, transparent 3px, transparent 8px),
    repeating-linear-gradient(to bottom, black 0px, black 3px, transparent 3px, transparent 8px),
    radial-gradient(ellipse ${size} ${size} at 50% 50%, #000 40%, transparent 100%)
  `

  return (
    <motion.div
      className={cn('absolute inset-0 z-0 dark:opacity-70', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, var(--border) 1px, transparent 1px),
          linear-gradient(to bottom, var(--border) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 0',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
